import { CbbAddDeduct, CbbProvince, CbbValuation, CbbVinQuery } from '../gql/generated/graphql';

export type CbbVehicle = NonNullable<NonNullable<CbbVinQuery['cbbVin']>['vehicles']>[number];

// CBB marks an option it applied from the VIN with auto = 'Y'.
const CBB_AUTO_APPLIED = 'Y';

const CBB_PROVINCES = new Set<string>(Object.values(CbbProvince));

const isCbbProvince = (region: string): region is CbbProvince => CBB_PROVINCES.has(region);

// The customer address region on a Canadian deal is a province abbreviation, which is what
// CBB takes; anything else (a US state on a mis-set deal) is not a province.
export const toCbbProvince = (region?: string | null): CbbProvince | undefined =>
  region != null && isCbbProvince(region) ? region : undefined;

// "2021 Toyota Rav4 XLE 4D Utility AWD"; a missing part leaves no double space.
export const cbbVehicleName = (
  vehicle: Pick<CbbVehicle, 'model_year' | 'make' | 'model'>,
): string => [vehicle.model_year, vehicle.make, vehicle.model].filter(Boolean).join(' ');

// The option codes CBB already applied from the VIN: a fresh trim pick starts with these.
export const appliedUocCodes = (options: Pick<CbbAddDeduct, 'uoc' | 'auto'>[]): string[] =>
  options.filter((option) => option.auto === CBB_AUTO_APPLIED).map((option) => option.uoc);

// The codes the calculator opens with: the saved selection when the saved trim is still
// listed, CBB's applied options for a fresh trim, nothing until a trim is picked.
export const initialUocCodes = (
  trim: Pick<CbbVehicle, 'uvc' | 'add_deduct_list'> | null,
  saved?: Pick<CbbValuation, 'uvc' | 'selected_options'> | null,
): string[] => {
  if (trim == null) {
    return [];
  }
  if (saved != null && saved.uvc === trim.uvc) {
    return saved.selected_options.map((option) => option.uoc);
  }
  return appliedUocCodes(trim.add_deduct_list);
};

// CBB can price a trim without returning its option metadata (empty all_options and
// selected_options beside valid values). Saving that wholesale would erase the option list
// and the selection the values were priced with, so the picked trim's own list and the
// requested codes fill the gap, as the API does at deal creation.
export const withTrimOptions = (
  valuation: CbbValuation,
  trimOptions: CbbAddDeduct[],
  uocCodes: string[],
): CbbValuation => {
  if (valuation.all_options.length > 0 || trimOptions.length === 0) {
    return valuation;
  }
  const codes = new Set(uocCodes);
  return {
    ...valuation,
    all_options: trimOptions,
    selected_options: trimOptions
      .filter((option) => codes.has(option.uoc))
      .map(({ uoc, name, clean }) => ({ uoc, name, clean })),
  };
};

// The applied codes after one toggle, in CBB's option order so the list is stable no matter
// which order the advisor clicked in.
export const toggleUocCode = (
  options: Pick<CbbAddDeduct, 'uoc'>[],
  uocCodes: string[],
  uoc: string,
): string[] => {
  const next = new Set(uocCodes);
  if (next.has(uoc)) {
    next.delete(uoc);
  } else {
    next.add(uoc);
  }
  return options.map((option) => option.uoc).filter((code) => next.has(code));
};
